import { createRngState, shuffle, nextRandom } from '../rng/rng';
import {
  STARTER_CARDS, COLONY_CARDS, EXPANSION_COLONIES, SHIPYARD_LEVEL_1, SHIPYARD_LEVEL_2, SHIPYARD_LEVEL_3,
} from '../cards/cardDefinitions.generated';
import { defFor } from '../cards/cardHelpers';
import {
  SECTORS, SHIPYARD_LEVELS, SHIPYARD_DISPLAY_SIZE, STARTING_CREDITS, TURN_ORDER_BONUSES,
} from '../constants';

let instanceCounter = 0;
function nextInstanceId() {
  instanceCounter += 1;
  return `inst-${instanceCounter}`;
}

// Test-only: makes instance ids deterministic/predictable across a single
// test file's createGameState() calls, mirroring the thunderstone repo's
// own injectInstance-style testing convention (an id like "inst-1" is
// otherwise dependent on how many games a given test file already made).
export function resetInstanceCounterForTests() {
  instanceCounter = 0;
}

function makeInstance(cardId) {
  return {
    instanceId: nextInstanceId(), cardId, stationCharges: 0, deployedCharges: 0,
  };
}

// Matt reported this directly: "The placeholder values for cards that we
// don't have artwork for don't exactly work. I'd rather just not include
// the cards." These 4 have a real public/card-art/<id>.jpg file, but a
// badly wrong crop from an earlier extraction run (320x28560 instead of
// the normal ~320x739 - see CardImage.vue's own onArtLoad comment and
// docs/card-audit.md's "Known-broken art files" section) - excluded from
// the shuffled deck entirely rather than ever being drawable and falling
// back to a text-only display in play.
const NO_USABLE_ART_CARD_IDS = new Set(['l1-bobko', 'l1-dzhanibekov', 'l1-ivanov', 'l1-romanenko']);

// Filtered once here, not inline in every caller - drawSetupCard below
// draws from this too (a raw, unfiltered SHIPYARD_LEVEL_1 reference
// there would let one of the 4 slip in during setup, bypassing the
// filtering in buildShipyard/levelDeckSource entirely).
const USABLE_SHIPYARD_LEVEL_1 = SHIPYARD_LEVEL_1.filter((c) => !NO_USABLE_ART_CARD_IDS.has(c.id));

function levelDeckSource(level) {
  if (level === 1) return USABLE_SHIPYARD_LEVEL_1;
  if (level === 2) return SHIPYARD_LEVEL_2;
  return SHIPYARD_LEVEL_3;
}

// Sets up a fresh command console: one starter card per sector 1-12.
function createStartingSectors() {
  const sectors = {};
  SECTORS.forEach((sectorNum) => {
    const def = STARTER_CARDS.find((c) => c.sector === sectorNum);
    sectors[sectorNum] = { station: makeInstance(def.id), deployedStack: [] };
  });
  return sectors;
}

// Setup step 5 (rulebook p10): each player draws one random Level 1 card,
// pays its cost, and immediately deploys it into their own board (same
// mechanic as a normal buy, minus the "spend ALL credits" rule - setup
// only deducts the exact cost). Returns the updated player plus the
// sector number their drawn card landed in (used to determine turn
// order - highest sector goes first).
//
// Matt reported this directly: "my starting card... a card that
// could've gone in any slot one or higher, but I wasn't given a choice
// of where to place it." Biodome's 4 Nomad Class ships are mixed into
// this same level-1 draw pool (per the Biodome rules card's own "shuffle
// the new ship cards into their corresponding decks" instruction - see
// docs/expansion-audit.md) and print "1+" instead of a fixed sector,
// same as any variable-sector card bought normally mid-game - this used
// to just silently deploy at the printed MINIMUM with no choice offered
// at all. Deploying (and paying for) it now defers to a real player
// choice (a pendingSetupPlacements entry, resolved via the new
// PLACE_SETUP_CARD action once the game state already exists) instead
// of guessing - only landedSector still uses the printed minimum, since
// turn order has to be determined synchronously here and doesn't depend
// on which of the legal sectors the player ends up actually choosing.
function drawSetupCard(player, rngState) {
  const [idx, nextRng] = (() => {
    const [v, next] = nextRandom(rngState);
    return [Math.floor(v * USABLE_SHIPYARD_LEVEL_1.length), next];
  })();
  const def = USABLE_SHIPYARD_LEVEL_1[idx];
  if (def.variableSector) {
    return {
      player,
      landedSector: def.sector,
      rngState: nextRng,
      pendingPlacement: { cardId: def.id, minSector: def.sector },
    };
  }
  const sector = player.sectors[def.sector];
  const bumped = sector.station;
  const newInstance = makeInstance(def.id);
  return {
    player: {
      ...player,
      credits: player.credits - def.cost,
      sectors: {
        ...player.sectors,
        [def.sector]: { station: newInstance, deployedStack: [...sector.deployedStack, bumped] },
      },
    },
    landedSector: def.sector,
    rngState: nextRng,
    pendingPlacement: null,
  };
}

function buildShipyard(rngState) {
  let rng = rngState;
  const draw = {};
  const display = {};
  SHIPYARD_LEVELS.forEach((level) => {
    const ids = levelDeckSource(level).map((c) => c.id);
    const [shuffled, nextRng] = shuffle(ids, rng);
    rng = nextRng;
    display[level] = shuffled.slice(0, SHIPYARD_DISPLAY_SIZE);
    draw[level] = shuffled.slice(SHIPYARD_DISPLAY_SIZE);
  });
  return { shipyard: { draw, display }, rngState: rng };
}

// Genesis's own "Set Up Colonies" rule (rulebook p2, verified word for
// word - the exact same wording Biodome's own setup uses, per that
// expansion's rules card): "Shuffle the new colony cards together...
// Reveal colony cards one at a time, until you have 6 from different
// sectors. If you draw a card and already have a colony for that sector,
// place the newly drawn card on the bottom of the deck and draw again.
// Use the regular colony cards from the base game for the remaining 6
// sectors." EXPANSION_COLONIES (buildCardData.mjs's own merge point -
// currently just Genesis's Peterschmidt, growing as more colonies become
// implementation-ready) is the shared candidate pool both expansions draw
// from, exactly as the rulebook itself expects ("if you have colonies
// from other expansions, you may shuffle them together with the new
// colonies as well") - one algorithm, not a per-expansion special case.
// A single already-shuffled pass through the pool is equivalent to
// "draw one at a time, discard duplicates to the bottom and keep going"
// - a duplicate sector's card can never become the sector's own chosen
// card no matter how many times it'd cycle back, so it only matters that
// each sector's FIRST appearance in shuffle order is the one kept.
function buildColonies(rngState, expansionCandidates) {
  const [shuffled, nextRng] = shuffle(expansionCandidates, rngState);
  const chosenBySector = new Map();
  shuffled.forEach((candidate) => {
    if (chosenBySector.size >= 6) return;
    if (!chosenBySector.has(candidate.sector)) chosenBySector.set(candidate.sector, candidate.id);
  });
  const colonies = SECTORS.map((sectorNum) => {
    const cardId = chosenBySector.get(sectorNum) || COLONY_CARDS.find((c) => c.sector === sectorNum).id;
    return { cardId, taken: false, takenByPlayerId: null };
  });
  return { colonies, rngState: nextRng };
}

const PLAYER_COLORS = ['Red', 'Orange', 'Yellow', 'Green', 'Teal', 'Blue', 'Purple'];

// playerConfigs: [{ id, name, isAI, difficulty }], 2-7 entries (base game
// supports 2-5; the TTS mod this was transcribed from extends to 7 -
// engine doesn't hard-cap it, but pip-scaled ability thresholds
// (constants.js) were only documented up to 5 players in the rulebook).
// isAI/difficulty mirror the thunderstone repo's own createGameState
// convention exactly (isAI: Boolean, difficulty: 'easy'|'normal'|'hard'
// unused by a human seat) - see engine/ai/heuristicBot.js's own comment
// for how difficulty is actually consumed.
export function createGameState(playerConfigs, seed) {
  let rng = createRngState(seed);

  let players = playerConfigs.map((cfg, i) => ({
    id: cfg.id,
    name: cfg.name,
    isAI: Boolean(cfg.isAI),
    difficulty: cfg.difficulty || 'normal',
    color: PLAYER_COLORS[i % PLAYER_COLORS.length],
    credits: STARTING_CREDITS,
    income: 0,
    vp: 0,
    sectors: createStartingSectors(),
  }));

  const setupDraws = [];
  const pendingSetupPlacements = [];
  players = players.map((player) => {
    const result = drawSetupCard(player, rng);
    rng = result.rngState;
    setupDraws.push({ playerId: player.id, landedSector: result.landedSector });
    if (result.pendingPlacement) {
      pendingSetupPlacements.push({ playerId: player.id, ...result.pendingPlacement });
    }
    return result.player;
  });

  let startingPlayerIndex = 0;
  let highestSector = -1;
  setupDraws.forEach((d, i) => {
    if (d.landedSector > highestSector) {
      highestSector = d.landedSector;
      startingPlayerIndex = i;
    }
  });
  // Ties broken by a dice roll (rulebook p10) - only among the tied
  // players, highest total wins; deterministic given the seeded RNG.
  const tied = setupDraws
    .map((d, i) => ({ i, sector: d.landedSector }))
    .filter((d) => d.sector === highestSector);
  if (tied.length > 1) {
    let bestTotal = -1;
    tied.forEach((t) => {
      const [d1, r1] = nextRandom(rng);
      const [d2, r2] = nextRandom(r1);
      rng = r2;
      const total = Math.floor(d1 * 6) + 1 + Math.floor(d2 * 6) + 1;
      if (total > bestTotal) {
        bestTotal = total;
        startingPlayerIndex = t.i;
      }
    });
  }

  // Turn-order starting bonuses (rulebook p10): applied in seat order
  // starting from the player immediately after the starting player.
  players = players.map((player, i) => {
    const seatOffset = (i - startingPlayerIndex + players.length) % players.length;
    const bonus = TURN_ORDER_BONUSES[seatOffset];
    if (!bonus) return player;
    return { ...player, [bonus.type]: player[bonus.type] + bonus.amount };
  });

  const shipyardResult = buildShipyard(rng);
  rng = shipyardResult.rngState;

  const coloniesResult = buildColonies(rng, EXPANSION_COLONIES);
  rng = coloniesResult.rngState;
  const { colonies } = coloniesResult;

  return {
    meta: {
      turnNumber: 1,
      activePlayerIndex: startingPlayerIndex,
      startingPlayerIndex,
      rngState: rng,
      turnPhase: 'pre-roll',
      dice: null,
      pendingSetupPlacements,
      gameEndTriggered: false,
      finalTurnPlayerIndex: null,
      winnerIds: null,
      log: [],
      // Matt reported this directly: "We need more stats on that endgame
      // screen... I want to see a distribution of the dice rolls. I want
      // to see total earnings [for] everybody." Neither is recoverable
      // from final state alone (a die's individual rolled value isn't
      // kept anywhere once allocated, and credits earned isn't the same
      // as credits held once any get spent) - accumulated as the game
      // goes instead. Matt, follow-up, once the individual-dice-only
      // version shipped: "I want to know the number of times every
      // number was rolled - if you roll a six and a three, add one to
      // the six column, one to the three column, and one to the nine
      // column." diceRollCounts spans every value that can actually come
      // up this way (1-6 for an individual die face, 2-12 for the
      // combined total) in one unified chart, not per-roll or per-player
      // - the dice are a shared resource every player allocates from,
      // not owned by whoever rolled them. totalCreditsEarned is
      // populated lazily (per player id) by dispatch()'s own
      // resource-gain diffing in engine/actions/index.js, not here.
      stats: {
        diceRollCounts: {
          1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0, 11: 0, 12: 0,
        },
        totalCreditsEarned: {},
      },
    },
    players,
    shipyard: shipyardResult.shipyard,
    colonies,
  };
}

export { makeInstance, defFor, buildColonies };
